const i18next = require('i18next')

const utils = require('./utils')
const appConfig = require('./config').getConfig()

// prepare i18next options based on appConfig.
const options = {
	ns: ['common','error','button'],
	defaultNS: 'common',
	debug: false,
	lng: 'de-at',
	supportedLngs: ['de-at'],
	saveMissing: true, // needed for missingKeyHandler to work.
	lowerCaseLng: true,
	initImmediate: false, // sync init

	...((appConfig.language || {}).i18next || {}),
}

const cores = {} // store i18next object instance by langCode
const translators = {} // store translator methods by langCode

// MARK: FEC_TRANSLATION_MANAGER
// this translationManager handles fec- / project-translation inheritance and i18next handling.
// just like the logHandler does one can get a registered function for reliably translating the given language without changing over time.
// all translations, languages, namespaces etc. are preloaded synchronously server- as well as client-side.

class TranslationManager {
	constructor() {
		this.initialized = false
	}

	/**
	 * initialize all languages including the given app translations
	 *
	 * @param translations		object - fec- / base-translations of the following format: {<langCode>: {<i18next-namespace>: {<translationKey>: <translationValue>}}}
	 * @param appTranslations	object - application translations of the following format to merge onto the given translations: {<langCode>: {<i18next-namespace>: {<translationKey>: <translationValue>}}}
	 */
	init(translations = {}, appTranslations = {}) {
		// set available resources
		options.resources = utils.simpleMerge(translations || {}, appTranslations || {})

		// prepare missingKeyHandler
		// MARK: TRANSLATE_ALREADY_TRANSLATED_MESSAGE_FOR_REPLACEMENTS_ONLY
		// sometimes the translators are used to replace variables within an already translated text.
		// these cases should not log missing translation keys.
		// one can set the _preventMissingKeyLog variable to true within the given options (replacement variables) to prevent these missing translations logs.
		// NOTE: since lngs will contain the fallback language in these cases it will not be very useful. to avoid confusion it will not be logged.
		options.missingKeyHandler = (lngs, ns, key, fallbackValue, updateMissing, options) => (options || {})._preventMissingKeyLog || this.logInfo('TRANSLATION_MISSING_KEY', {ns, key})

		// collect all supported languages based on options and available languages.
		const supportedLngs = {}
		for(const langCode of Object.values(options.supportedLngs || {}).concat(Object.keys(options.resources || {}))){
			supportedLngs[langCode] = true
		}

		// since all translations will be given directly without any backend logic, all supported languages should be marked to be preloaded.
		options.preload = options.supportedLngs = Object.keys(supportedLngs)

		// init i18next
		i18next.init(options)

		// clone i18next object for each langCode
		for(const langCode of Object.values(options.supportedLngs || {})){
			cores[langCode] = i18next.cloneInstance({
				lng: langCode,
			})

			// register translator by langCode
			translators[langCode] = (...args) => cores[langCode].t.apply(cores[langCode], args)
		}
	}

	/**
	 * returns a function according to the given langCode used for translating an i18next key.
	 * if the langCode is empty or the corresponding translator could not be found an error message will be logged.
	 *
	 * @see https://www.i18next.com/overview/api#t
	 *
	 * @param langCode		string - langCode
	 * @returns {(function(i18nextKeys, i18nextOptions): string)}
	 */
	getTranslator(langCode){
		// use base language as fallback just in case there is no translator registered with the specific langCode given
		// e.g., use "de" directly if "de-at" could not be found.
		// i18next would fall back to the base language automatically, but this way no extra runtime performance is needed.
		const [fallbackLangCode] = String(langCode || '').split('-')
		const translator = (translators || {})[langCode || '~~~MISSING~LANG~CODE~~~'] || (translators || {})[fallbackLangCode || '~~~MISSING~LANG~CODE~~~']
		if(!translator){
			return (translators[langCode || '~~~MISSING~LANG~CODE~~~'] = (...args) => this.logError('MISSING_TRANSLATOR_USAGE', langCode, ...args))
		}
		return translator
	}

	/**
	 * this method is used to map all languages (appConfig.language.available) to some sort of object or array.
	 *
	 * @param callback				function(translator: function, langCode: string, availableLanguageConfig: object) - has to return some value per given params to write into an object.
	 * @param storage             	object - actual storage
	 * @param requestedServiceCode  string | boolean - false => ownService, true => webService, string => requestedServiceCode within assignedServices.
	 * @returns {*|{[langCode: string]: [callbackReturn: *]}} e.g., {'de-at': { name: 'test'}}
	 */
	mapLanguages(callback, storage, requestedServiceCode = 'web'){
		if(!utils.isFunction(callback)){
			return this.logError('TRANSLATOR_MAP_ALL_MISSING_CALLBACK')
		}

		return utils.mapValue(utils.language.getConfig(storage, requestedServiceCode).available, (availableLanguageConfig, langCode) => callback(translators[langCode] || translationManager.getTranslator(langCode), langCode, availableLanguageConfig))
	}

	/**
	 * log info
	 */
	logInfo(){
		// NOTE: overwritten in server or client
		console.log(...arguments)
	}

	/**
	 * log error
	 */
	logError(){
		// NOTE: overwritten in server or client
		console.error(...arguments)
	}
}

module.exports = TranslationManager